import { Link, useNavigate } from 'react-router-dom';
import { buildDocumentSharePageV2Url } from 'utils/buildShareDocumentV2Link';
import time from 'utils/time';
import { UploadHubListItem } from 'api/services/uploadFileService';
import { getFileBadge } from 'components/UploadHub/fileTypeBadges';
import { useRef } from 'react';

interface UploadHubItemProps {
  file: UploadHubListItem;
  deleteItemState: {
    setIsOpen: (value: boolean) => void;
    setItemId: (value: string | number | null) => void;
  };
}

const UploadHubItem = (props: UploadHubItemProps) => {
  const { file, deleteItemState } = props ?? {};
  const { setIsOpen, setItemId } = deleteItemState ?? {};

  const navigate = useNavigate();

  const { id, url, fileName, createdAtUtc, addedByUserFullName, publicId } = file;
  const badge = getFileBadge(fileName);
  const viewButton = useRef<HTMLAnchorElement>(null);
  const deleteButton = useRef<HTMLButtonElement>(null);
  const emailButton = useRef<HTMLButtonElement>(null);
  const lobbyingMaterialButton = useRef<HTMLButtonElement>(null);

  return (
    <li className='p-3 mb-2' key={`upload-hub-file-${id ?? url}`}>
      <div className='d-flex report-main-list-item justify-content-between align-items-center'>
        <div className='d-flex'>
          <span
            className={`paragraph-p6 font-weight-bold mr-3 upload-hub-file-icon ${badge.className} text-white text-center flex-centered justify-content-center`}
          >
            {badge.label}
          </span>
          <div>
            <h4 className='content-item-title mb-0'>{fileName}</h4>
            {createdAtUtc && (
              <p className='text-grey-description mt-1 mb-0 paragraph-p3'>
                Uploaded on {time(createdAtUtc).formatGeneralDate()}{' '}
                <span className='heading-date'>
                  <span className='dot' /> {addedByUserFullName}
                </span>
              </p>
            )}
          </div>
        </div>
        <div className='flex-centered'>
          <Link
            to={buildDocumentSharePageV2Url(publicId)}
            target='_blank'
            rel='noreferrer'
            className='general-button circle-button hovered-button reset-link reset-link-color flex-centered justify-content-center'
            data-tooltip-id='general-tooltip'
            data-tooltip-content={'View file'}
            ref={viewButton}
            onClick={() => {
              viewButton.current?.blur();
            }}
          >
            <span className='icon-visibility paragraph-p2' />
          </Link>
          <button
            className='mx-2 general-button circle-button circle-button-36 hovered-button flex-centered justify-content-center'
            data-tooltip-id='general-tooltip'
            data-tooltip-content={'Add to email'}
            ref={emailButton}
            onClick={() => {
              emailButton.current?.blur();
              navigate(
                { pathname: '/people/emails/create' },
                {
                  state: {
                    prepopulatedAttachment: {
                      url: buildDocumentSharePageV2Url(file.publicId),
                      fileName: file.fileName,
                    },
                  },
                }
              );
            }}
          >
            <span className='icon-email paragraph-p3' />
          </button>
          <button
            ref={lobbyingMaterialButton}
            className='general-button circle-button circle-button-36 hovered-button flex-centered justify-content-center'
            data-tooltip-id='general-tooltip'
            data-tooltip-content={'Add to lobbying material'}
            onClick={() => {
              lobbyingMaterialButton.current?.blur();
              navigate(
                { pathname: '/brief-policymakers/briefing-material/create/2' },
                {
                  state: {
                    headline: file.fileName,
                    attachments: { savedFiles: [{ url: file.url, name: file.fileName }] },
                  },
                }
              );
            }}
          >
            <span className='icon-briefing-material paragraph-p1' />
          </button>
          <button
            className='ml-1 general-button circle-button circle-button-36 hovered-button flex-centered justify-content-center'
            data-tooltip-id='general-tooltip'
            data-tooltip-content={'Delete item'}
            ref={deleteButton}
            onClick={() => {
              deleteButton.current?.blur();
              setItemId(id);
              setIsOpen(true);
            }}
          >
            <span className='icon-delete paragraph-p1' />
          </button>
        </div>
      </div>
    </li>
  );
};

export default UploadHubItem;
